import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Card, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/Dashboard.css';
import apiService from '../services/apiService';
import OrderDetailsTable from '../components/OrderDetailsTable';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_runs: 0,
    successful_runs: 0,
    failed_runs: 0,
    completion_status_counts: {}
  });

  const [estimatedClosingData, setEstimatedClosingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('Dashboard component mounted - initializing API calls');
    
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDashboardStats();
        setStats(response.automation);

        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Function to fetch estimated closing data
    const fetchEstimatedClosingData = async () => {
      try {
        // Use today's date as default expected_date
        const today = new Date().toISOString().split('T')[0];
        console.log('Fetching estimated closing data for date range: 1 month ago to today');
        
        const response = await apiService.getEstimatedClosingData();
        console.log('Estimated Closing Data received:', response);
        setEstimatedClosingData(response);
      } catch (err) {
        console.error('Error fetching estimated closing data:', err);
        // Don't set error state for estimated closing as it's supplementary data
      }
    };

    // Initial fetch
    console.log('Starting initial data fetch...');
    fetchDashboardStats();
    fetchEstimatedClosingData();

    // Listen for dashboard updates from Sidebar WebSocket
    const handleDashboardUpdate = (event) => {
      const data = event.detail;
      console.log('Dashboard update received from Sidebar:', data);
      if (data.stats) {
        setStats(prevStats => ({
          ...prevStats,
          ...data.stats,
          completion_status_counts: {
            ...prevStats.completion_status_counts,
            ...(data.stats.completion_status_counts || {})
          }
        }));
      }
      
      // Handle estimated closing data updates via WebSocket
      if (data.estimated_closing) {
        console.log('Estimated Closing WebSocket update received:', data.estimated_closing);
        setEstimatedClosingData(data.estimated_closing);
      }
    };

    // Add event listener for dashboard updates from Sidebar
    window.addEventListener('dashboardUpdate', handleDashboardUpdate);

    // Cleanup function
    return () => {
      window.removeEventListener('dashboardUpdate', handleDashboardUpdate);
    };
  }, []);

  // Calculate success rate
  const successRate = stats.total_runs > 0 ? ((stats.successful_runs / stats.total_runs) * 100).toFixed(1) : 0;
  const failureRate = stats.total_runs > 0 ? ((stats.failed_runs / stats.total_runs) * 100).toFixed(1) : 0;



  // Bar chart data for recent runs
  const barData = {
    labels: ['Total Runs', 'Successful', 'Failed'],
    datasets: [
      {
        label: 'Automation Triggers',
        backgroundColor: [
          'rgba(78, 115, 223, 0.8)',
          'rgba(28, 200, 138, 0.8)',
          'rgba(231, 74, 59, 0.8)'
        ],
        borderColor: [
          'rgba(78, 115, 223, 1)',
          'rgba(28, 200, 138, 1)',
          'rgba(231, 74, 59, 1)'
        ],
        borderWidth: 2,
        hoverBackgroundColor: [
          'rgba(78, 115, 223, 0.9)',
          'rgba(28, 200, 138, 0.9)',
          'rgba(231, 74, 59, 0.9)'
        ],
        data: [stats.total_runs, stats.successful_runs, stats.failed_runs],
        borderRadius: 4,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: '#858796'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        ticks: {
          color: '#858796'
        },
        grid: {
          display: false
        }
      }
    }
  };

  // Completion Status chart data with detailed bar chart styling
  const completionStatusData = {
    labels: [
      'BAP',
      'LAO', 
      'SAO',
      'FSBO-NA',
      'ACM',
      'BAM',
      'Refinance'
    ],
    datasets: [
      {
        label: 'Completion Count',
        backgroundColor: [
          'rgba(76, 175, 80, 0.8)',   // Green for Both Agents Present
          'rgba(255, 193, 7, 0.8)',   // Yellow for Listing Agents Only
          'rgba(33, 150, 243, 0.8)',  // Blue for Selling Agents Only
          'rgba(158, 158, 158, 0.8)', // Gray for FSBO
          'rgba(255, 152, 0, 0.8)',   // Orange for Agent Copy Missing
          'rgba(244, 67, 54, 0.8)',   // Red for Both Agents Missing
          'rgba(156, 39, 176, 0.8)'   // Purple for Refinance
        ],
        borderColor: [
          'rgba(76, 175, 80, 1)',
          'rgba(255, 193, 7, 1)',
          'rgba(33, 150, 243, 1)',
          'rgba(158, 158, 158, 1)',
          'rgba(255, 152, 0, 1)',
          'rgba(244, 67, 54, 1)',
          'rgba(156, 39, 176, 1)'
        ],
        borderWidth: 1,
        hoverBackgroundColor: [
          'rgba(76, 175, 80, 0.9)',
          'rgba(255, 193, 7, 0.9)',
          'rgba(33, 150, 243, 0.9)',
          'rgba(158, 158, 158, 0.9)',
          'rgba(255, 152, 0, 0.9)',
          'rgba(244, 67, 54, 0.9)',
          'rgba(156, 39, 176, 0.9)'
        ],
        hoverBorderColor: [
          'rgba(56, 142, 60, 1)',
          'rgba(245, 124, 0, 1)',
          'rgba(25, 118, 210, 1)',
          'rgba(117, 117, 117, 1)',
          'rgba(216, 67, 21, 1)',
          'rgba(211, 47, 47, 1)',
          'rgba(123, 31, 162, 1)'
        ],
        hoverBorderWidth: 3,
        data: [
          stats.completion_status_counts?.both_agents_present || 0,
          stats.completion_status_counts?.listing_agents_only || 0,
          stats.completion_status_counts?.selling_agents_only || 0,
          stats.completion_status_counts?.fsbo_no_agents || 0,
          stats.completion_status_counts?.agent_copy_missing || 0,
          stats.completion_status_counts?.both_agents_missing || 0,
          stats.completion_status_counts?.refinance || 0
        ],
        borderRadius: 0,
        barThickness: 80,
        maxBarThickness: 90
      },
    ],
  };

  const completionStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
      delay: (context) => {
        return context.dataIndex * 150;
      }
    },
    elements: {
      bar: {
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0
        },
        borderSkipped: false
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: true,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const value = context.parsed.y;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `Count: ${value.toLocaleString()} (${percentage}%)`;
          },
          afterLabel: function(context) {
            const descriptions = {
              'Both Agents Present': 'Complete agent information available',
              'Listing Agents Only': 'Only listing agent details found',
              'Selling Agents Only': 'Only selling agent details found',
              'FSBO - No Agents': 'For Sale By Owner transactions',
              'Agent Copy Missing': 'Agent data incomplete or missing',
              'Both Agents Missing': 'No agent information available',
              'Refinance': 'Refinance transaction processing'
            };
            return descriptions[context.label] || '';
          },
          footer: function(tooltipItems) {
            const total = tooltipItems[0].dataset.data.reduce((a, b) => a + b, 0);
            return `Total Records: ${total.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(158, 158, 158, 0.1)',
          lineWidth: 1,
          drawBorder: false
        },
        border: {
          display: false
        },
        ticks: {
          precision: 0,
          color: '#757575',
          font: {
            size: 12,
            family: 'Roboto, sans-serif',
            weight: '400'
          },
          padding: 8,
          callback: function(value) {
            return value.toLocaleString();
          }
        },
      },
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          color: '#424242',
          font: {
            size: 11,
            weight: '500',
            family: 'Roboto, sans-serif'
          },
          padding: 8,
          maxRotation: 45,
          minRotation: 0
        }
      }
    },
    layout: {
      padding: {
        top: 5,
        right: 10,
        bottom: 5,
        left: 5
      }
    }
  };

  return (
    <div className="container-fluid mt-4">
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 mb-2 mt-2 text-gray-800">
            <i className="fas fa-tachometer-alt me-2"></i>
            Analytics
          </h1>
          <p className="text-muted mb-0">Automation triggers overview and analytics</p>
        </div>
        
      </div>

      {/* Global Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4" dismissible>
          <Alert.Heading>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Error Loading Dashboard
          </Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      )}

      {/* Stats Cards Row */}
      <Row className="g-3 mb-2">
        {/* Total Runs Card */}
        <Col xl={3} md={6}>
          <Card className="border-0 shadow-sm h-100" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                  Total Automation Triggers
                </div>
                <div className="h4 mb-0 fw-bold">
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <span className="counter">{stats.total_runs.toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="ms-3">
                <i className="fas fa-play-circle fa-2x opacity-75"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Successful Runs Card */}
        <Col xl={3} md={6}>
          <Card 
            className="border-0 shadow-sm h-100" 
            style={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/automation-runs')}
          >
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                  Successful Triggers
                </div>
                <div className="h4 mb-0 fw-bold">
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <span className="counter">{stats.successful_runs.toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="ms-3">
                <i className="fas fa-check-circle fa-2x opacity-75"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Failed Runs Card */}
        <Col xl={3} md={6}>
          <Card 
            className="border-0 shadow-sm h-100" 
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
              color: 'white',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/automation/failed')}
          >
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                  Failed Triggers
                </div>
                <div className="h4 mb-0 fw-bold">
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <span className="counter">{stats.failed_runs.toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="ms-3">
                <i className="fas fa-times-circle fa-2x opacity-75"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Success Rate Card */}
        <Col xl={3} md={6}>
          <Card className="border-0 shadow-sm h-100" style={{
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            color: '#333'
          }}>
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <div className="text-muted small text-uppercase fw-bold mb-1">
                  Success Rate
                </div>
                <div className="h4 mb-0 fw-bold">
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <span className="counter">{successRate}%</span>
                  )}
                </div>
                <div className="small">
                  <Badge bg={successRate >= 80 ? 'success' : successRate >= 60 ? 'warning' : 'danger'}>
                    {successRate >= 80 ? 'Excellent' : successRate >= 60 ? 'Good' : 'Needs Attention'}
                  </Badge>
                </div>
              </div>
              <div className="ms-3">
                <i className="fas fa-chart-line fa-2x opacity-75"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      
      

      {/* Charts Row */}
      <Row className="g-4 mb-2">
      

        {/* Bar Chart */}
        <Col xl={6} lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0 fw-bold text-primary">
                  <i className="fas fa-chart-bar me-2"></i>
                  Automation Triggers 
                </h6>
                <div className="d-flex align-items-center">
                  <small className="text-muted me-3">Total: {stats.total_runs.toLocaleString()}</small>
                  <small className="text-muted me-3">Successful: {stats.successful_runs.toLocaleString()} ({successRate}%)</small>
                  <small className="text-muted me-3">Failed: {stats.failed_runs.toLocaleString()} ({failureRate}%)</small>
                  <Badge bg="primary" className="px-2 py-1">
                    Live Data
                  </Badge>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Loading chart data...</p>
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                  <p className="text-muted">{error}</p>
                </div>
              ) : (
                <div style={{ height: '300px' }}>
                  <Bar data={barData} options={barOptions} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="mb-0 fw-bold text-primary">
                  <i className="fas fa-users me-2"></i>
                  Agent Copies Segregation
                </h6>
                <div className="d-flex align-items-center">
                  <small className="text-muted me-3">
                    Total Records: {Object.values(stats.completion_status_counts || {}).reduce((a, b) => a + b, 0).toLocaleString()}
                  </small>
                  <small className="text-muted me-3">
                    Categories: {Object.keys(stats.completion_status_counts || {}).length}
                  </small>
                  <Badge bg="success" className="px-2 py-1">
                    Agent Analysis
                  </Badge>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Loading completion status data...</p>
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                  <p className="text-muted">{error}</p>
                </div>
              ) : (
                <div style={{ height: '400px' }}>
                  <Bar data={completionStatusData} options={completionStatusOptions} />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Completion Status Chart Row */}
      <Row className="g-2 mb-2">
        
      </Row>

      
    </div>
  );
};

export default Dashboard;